// youtube/directions.tsx — THE SIX FOOTAGE-LED DIRECTIONS, as presentations.
//
// Generated by agents/remy/slate-video/scripts/gen-directions-section.mjs.
// Do not hand-edit: runtimes and slide counts come from the files that were
// actually rendered and uploaded.
//
// WHY THESE EXIST: Erez watched the original ten-film slate on 13 Aug 2026 and
// said "these videos are very static, they feel more like a pitch deck than a
// video." He was right — every one of the ten was type on a flat colour field.
// These six invert it: real footage fills the frame and the graphics ride on
// top. All stock is licensed for commercial use (Pexels, archive.org public
// domain, Wikimedia Commons) and NO frame is AI-generated video.
//
// Board law: a board is DocumentFrame | PresentationFrame | code diagram.
// This is the second kind.

import { PresentationFrame } from "../PresentationFrame";

const R2 = "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/board/youtube/directions";

type Entry = {
  slug: string; name: string; title: string; channel: string; kind: string;
  pitch: string; reference: string; voice: string; portrait: boolean;
  slideCount: number; duration: string;
};

const DIRECTIONS: Entry[] = [
  {
    "slug": "d1-cinematic-chokepoints",
    "name": "Cinematic Documentary",
    "title": "The Four Places Every Ship On Earth Passes Through",
    "channel": "The World Map",
    "kind": "cinematicDoc",
    "pitch": "Cinematic documentary — 4K footage full-frame, slow push-ins, letterbox, chapter cards.",
    "reference": "Johnny Harris · Wendover · Like Stories of Old",
    "voice": "am_onyx",
    "portrait": false,
    "slideCount": 7,
    "duration": "0:38"
  },
  {
    "slug": "d2-archive-first-impressions",
    "name": "Archive Essay",
    "title": "You Decided About Them Before They Spoke",
    "channel": "Human Nature",
    "kind": "archiveEssay",
    "pitch": "Archive essay — public-domain film, grain, 4:3 inserts in a 16:9 field, typewriter titles.",
    "reference": "exurb1a · The Paint Explainer",
    "voice": "bm_george",
    "portrait": false,
    "slideCount": 7,
    "duration": "0:32"
  },
  {
    "slug": "d3-fasttech-five-tools",
    "name": "Fast Tech Explainer",
    "title": "Five AI Tools Doing Real Work In Real Businesses",
    "channel": "Stacked",
    "kind": "fastTech",
    "pitch": "Fast tech explainer — hard cuts, punch-ins, keyword flashes, two-second beats, zero dead air.",
    "reference": "Fireship · AI Explained",
    "voice": "am_michael",
    "portrait": false,
    "slideCount": 7,
    "duration": "0:32"
  },
  {
    "slug": "d4-databrief-unread-papers",
    "name": "Data Brief",
    "title": "The Machines Are Reading The Papers Nobody Read",
    "channel": "AI Discoveries",
    "kind": "dataBrief",
    "pitch": "Data brief — footage under animated count-ups, lower-thirds and a ticker. The figure is the star.",
    "reference": "Vox · TLDR News · Perun",
    "voice": "bm_fable",
    "portrait": false,
    "slideCount": 6,
    "duration": "0:28"
  },
  {
    "slug": "d5-mapmotion-sea-cables",
    "name": "Map Motion",
    "title": "The Quiet Fight Over The Cables Under The Sea",
    "channel": "The World Map",
    "kind": "mapMotion",
    "pitch": "Map motion — an animated map as the continuous spine, footage cutaways for texture.",
    "reference": "CaspianReport · RealLifeLore",
    "voice": "am_adam",
    "portrait": false,
    "slideCount": 7,
    "duration": "0:33"
  },
  {
    "slug": "d6-short-picked-a-side",
    "name": "Vertical Short",
    "title": "You Picked A Side Before You Finished Reading",
    "channel": "Shorts (any channel)",
    "kind": "verticalShort",
    "pitch": "Vertical short — 9:16, footage-led, burned captions, jump cuts, one idea, thirty seconds.",
    "reference": "Fireship shorts · Alex Christian",
    "voice": "am_puck",
    "portrait": true,
    "slideCount": 5,
    "duration": "0:16"
  },
  {
    "slug": "t1-travel-death-road",
    "name": "The Death Road",
    "title": "The Road That Killed 300 People A Year — And The Queue To Ride It",
    "channel": "The Places",
    "kind": "cinematicDoc",
    "pitch": "Travel pilot 1, rebuilt — one clip per beat, graded, with room to breathe.",
    "reference": "Johnny Harris · Wendover · Best Documentary",
    "voice": "bm_george",
    "portrait": false,
    "slideCount": 42,
    "duration": "3:31"
  },
  {
    "slug": "g1-gatea-bill-nobody-signed",
    "name": "The Bill Nobody Signed",
    "title": "The Bill Nobody Signed",
    "channel": "Follow The Money",
    "kind": "dataBrief",
    "pitch": "Gate A cut — the opening ninety seconds of episode one, figures over footage, ending on a market at its own ceiling.",
    "reference": "The Modern MBA · Wendover",
    "voice": "am_puck",
    "portrait": false,
    "slideCount": 11,
    "duration": "1:35"
  }
];

/** The films alone, no container — the board wraps them in its gallery.
 *
 * ONE BIG NAME ABOVE EACH FILM, and nothing else. Erez, 13 Aug: "give each of
 * these types a name with a nice title above it. No small texts and
 * descriptions." The old version carried a label bar of
 * channel-dot-kind-dot-voice-dot-runtime plus a caption paragraph underneath —
 * a wall of 11px grey that nobody reads and that made a shelf of films look
 * like a spreadsheet. The frames themselves say what they are. */
export function DirectionFilmGrid({ cols = 3 }: { cols?: number }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, max-content)`, gap: 72 }}>
      {DIRECTIONS.map((v) => (
        <div key={v.slug} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <div
            style={{
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
              fontSize: 38,
              fontWeight: 800,
              letterSpacing: "-0.025em",
              color: "#1C1917",
              lineHeight: 1.1,
            }}
          >
            {v.name}
          </div>
          <PresentationFrame
            videoSrc={`${R2}/${v.slug}/film.mp4`}
            poster={`${R2}/${v.slug}/poster.jpg`}
            slidesBase={`${R2}/${v.slug}/slides`}
            slideCount={v.slideCount}
            width={v.portrait ? 486 : 864}
            aspect={v.portrait ? 1080 / 1920 : 16 / 9}
          />
        </div>
      ))}
    </div>
  );
}

export const DIRECTION_COUNT = 8;
